import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { ExternalLink, CheckCircle, Hourglass } from 'lucide-react';
import { chatbotAPI } from '../../services/chatbotService';

const FeedbackList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async (currentFilter) => {
    try { setLoading(true); setError(''); const params = { limit: 100 }; if (currentFilter === 'pending') params.pending_only = true; if (currentFilter === 'completed') params.completed_only = true; if (currentFilter === 'negative') params.negative_only = true; const resp = await chatbotAPI.getFeedbackList(params); setItems(resp.data.items || []); }
    catch { setError('Failed to load feedback list.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const formatDate = (dateStr) => { if (!dateStr) return '—'; return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className="max-w-5xl mx-auto py-6">
      <Card className="mb-4"><CardContent className="p-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Feedback List</h2>
          <ToggleGroup value={filter} onChange={setFilter}>
            <ToggleItem value="all">All</ToggleItem>
            <ToggleItem value="pending">Without LLM evaluation</ToggleItem>
            <ToggleItem value="completed">With LLM evaluation</ToggleItem>
            <ToggleItem value="negative">Negative feedback</ToggleItem>
          </ToggleGroup>
        </div>
      </CardContent></Card>

      {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      {loading ? <div className="flex justify-center py-8"><Spinner /></div> : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableHead>Thread ID</TableHead>
                <TableHead className="text-center">Turns evaluated</TableHead>
                <TableHead className="text-center">LLM status</TableHead>
                <TableHead>Last feedback</TableHead>
                <TableHead className="text-center">Open</TableHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-ink-dim">No chats found.</TableCell></TableRow>
              ) : items.map((item) => {
                const allAnalyzed = item.pending_llm_count === 0;
                return (
                  <TableRow key={item.thread_id} className="cursor-pointer" onClick={() => navigate(`/bot-evaluation/${encodeURIComponent(item.thread_id)}`)}>
                    <TableCell className="font-mono text-xs">{item.thread_id}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary">{item.evaluation_count}</Badge></TableCell>
                    <TableCell className="text-center">
                      {allAnalyzed ? (
                        <Tooltip><TooltipTrigger asChild><span><CheckCircle className="h-4 w-4 text-brand-green inline" /></span></TooltipTrigger><TooltipContent>LLM analysis completed</TooltipContent></Tooltip>
                      ) : (
                        <Tooltip><TooltipTrigger asChild><span><Hourglass className="h-4 w-4 text-brand-yellow inline" /></span></TooltipTrigger><TooltipContent>{item.pending_llm_count} turn(s) pending LLM analysis</TooltipContent></Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="text-ink-dim text-xs">{formatDate(item.last_feedback_at)}</TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => navigate(`/bot-evaluation/${encodeURIComponent(item.thread_id)}`)} className="p-1 text-ink-dim hover:text-ink"><ExternalLink className="h-4 w-4" /></button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default FeedbackList;
